import type { ReactNode } from 'react';
import {
  DukantiBrandMark,
  normalizeLocale,
  useTranslation,
  writePersistedLocale,
} from '@dukanti/shared';
import type { SupportedLocale, TranslationKey } from '@dukanti/shared';
import { cn } from '@/lib/utils';

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  testId?: string;
}

export function AuthLayout({ title, subtitle, children, testId }: AuthLayoutProps) {
  const { dir } = useTranslation();
  return (
    <main
      dir={dir}
      data-testid={testId}
      className="bg-background flex min-h-screen items-center justify-center px-4 py-10 sm:py-12"
    >
      <div className="bg-card text-card-foreground border-border w-full max-w-md rounded-xl border p-6 shadow-sm sm:p-8">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3" aria-hidden="true">
            <DukantiBrandMark className="size-9 rounded-lg" />
            <span className="text-foreground text-sm font-semibold tracking-wide">Dukanti</span>
          </div>
          <AdminAuthLanguageSwitcher />
        </div>
        <h1 className="font-display text-foreground text-start text-3xl">{title}</h1>
        {subtitle ? <p className="text-muted-foreground mt-2 text-start">{subtitle}</p> : null}
        <div className="mt-6">{children}</div>
      </div>
    </main>
  );
}

interface LocaleOption {
  readonly value: SupportedLocale;
  readonly label: string;
  readonly lang: string;
  readonly labelKey: TranslationKey;
}

const LOCALE_OPTIONS: readonly LocaleOption[] = [
  { value: 'ar', label: 'ع', lang: 'ar', labelKey: 'common.languageNames.ar' },
  { value: 'he', label: 'ע', lang: 'he', labelKey: 'common.languageNames.he' },
  { value: 'en', label: 'EN', lang: 'en', labelKey: 'common.languageNames.en' },
] as const;

function AdminAuthLanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const locale = normalizeLocale(i18n.language);

  const changeLanguage = (next: SupportedLocale): void => {
    writePersistedLocale(next);
    void i18n.changeLanguage(next);
  };

  return (
    <nav
      aria-label={t('admin.nav.languageSwitcher')}
      className="border-border bg-background inline-flex shrink-0 items-center overflow-hidden rounded-md border"
      data-testid="admin-auth-language-switcher"
    >
      {LOCALE_OPTIONS.map((option, idx) => {
        const isActive = locale === option.value;
        return (
          <button
            key={option.value}
            type="button"
            lang={option.lang}
            aria-label={t(option.labelKey)}
            aria-pressed={isActive}
            data-testid={`admin-auth-locale-${option.value}`}
            onClick={() => changeLanguage(option.value)}
            className={cn(
              'focus-visible:ring-ring inline-flex h-8 min-w-8 items-center justify-center px-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
              idx > 0 && 'border-border border-s',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </nav>
  );
}
